import React, { useEffect, useState } from 'react';
import { FlatList, TouchableOpacity } from 'react-native';
import { Icons } from '../../Components';
import type { PasswordStateProps } from '../api/types';
import Colors from '../../constants/colors';
import { ProfileApi, ProfileHelpers } from '../api';
import { button_styles } from '../../constants/styles';
import type { FocusPositionProps, MyPlayerProps } from '../../types';
import { Text, View, Button, TextInput } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type PasswordManagerProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    onNext:() => void,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onClose:() => void,
    onPlayerUpdate:(player:MyPlayerProps) => void
}

const sections = ['header','new_password','change_password'];
const PasswordManager = ({ player, walkthrough, onPlayerUpdate, onFocusPosition, onNext, onClose }:PasswordManagerProps) => {
    const C = useColors();
    const [ draft_player, setDraftPlayer ] = useState<MyPlayerProps>(player);
    const [ draft_password, setDraftPassword ] = useState({
        change_visible:false,
        loading:false,
        current_password: '',
        password: '',
        re_password: ''
    })
 
    const { change_visible, password, re_password, current_password } = draft_password;

    useEffect(() => {
        setDraftPlayer(player)
    },[player])


    let password_states:PasswordStateProps[] = [ 
        { attribute:'password', type:'min_character', state: 'not_started', label:'At least 8 characters' }, 
        { attribute:'password', type: 'case', state:'not_started', label:'Uppercase and lowercase character' }, 
        { attribute:'password', type:'number', state:'not_started', label: 'Must use a number' }, 
        { attribute:'password', type:'special', state:'not_started', label: 'Includes at least 1 special character' },
        { attribute:'rePassword', type:'special', state:'not_started', label: 'Passwords do not match' },
    ]

    const validatePassword = () => {
        let valid_states:PasswordStateProps[] = []
        password_states.map(ps => {
            if(ps.attribute == 'rePassword'){
                if(re_password != password){
                    valid_states.push({ ...ps, state: 'invalid' })
                } else {
                    valid_states.push({ ...ps, state: 'valid' })
                }
            } else {
                valid_states.push(ProfileHelpers.isPasswordValid(password, ps))
            }
        })
        return valid_states
    }



    /**
     * Flatlist render function to render all the password statuses and their states
     * @param data 
     * @returns 
     */
    const renderPasswordStatuses = (data: { item:PasswordStateProps, index:number }) => {
        return (
            <View style={{flex:1, flexDirection:'row', alignItems:'center', padding:8}}>
                <Text style={{ flex:1}} theme='h2'>{data.item.label}</Text>
                {data.item.state === 'valid' ?
                <Icons.CheckCirlceIcon size={14} color={C.text.success} />
                :
                <Icons.AlertIcon size={14} color={C.text.warning} />
                }
            </View>
        )
    }

    const valid_states = validatePassword()
    const is_valid = !valid_states.find(vs => ['invalid'].includes(vs.state)) ? true : false


    const handleSetPassword = async() => {
        if(!password){ return onNext() }
        setDraftPassword({ ...draft_password, loading:true })
        const player = await ProfileApi.setPassword(password, re_password, current_password)
        onPlayerUpdate(player);
        setTimeout(() => {
            setDraftPassword({ current_password:'', change_visible: false, loading:false, password: '', re_password: '' })
            if(walkthrough){ onNext() } else { onClose() }
        }, 500);
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!walkthrough){ return <></> }
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>Secure Your Account</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>Help up secure your account with a password. Once set, you can login using your username and password</Text>
                    </View>
                )
            case 'new_password':
                return (
                    <View style={{ padding:20 }}>
                        {!draft_player.no_password ?
                        <View type='input' style={{ margin:10, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.LockClosedIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={draft_password.current_password}
                                placeholder='My Current Password'
                                onFocusPosition={onFocusPosition}
                                secureTextEntry
                                style={{ flex:1 }}
                                onChangeText={(text) => setDraftPassword({ ...draft_password, current_password : text })}
                            />
                        </View>
                        :<></>}
                        <View type='input' style={{ margin:10, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.LockClosedIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={draft_password.password}
                                placeholder='Enter Password'
                                onFocusPosition={onFocusPosition}
                                secureTextEntry
                                style={{ flex:1 }}
                                onChangeText={(text) => setDraftPassword({ ...draft_password, password : text })}
                            />
                        </View>
                        <View type='input' style={{ margin:10, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.LockClosedIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={draft_password.re_password}
                                placeholder='Re-Enter Password'
                                onFocusPosition={onFocusPosition}
                                secureTextEntry
                                style={{ flex:1  }}
                                onChangeText={(text) => setDraftPassword({ ...draft_password, re_password : text })}
                            />
                        </View>
                        <View style={{ padding:10 }}>
                            <FlatList data={valid_states} renderItem={renderPasswordStatuses} keyExtractor={(item) => item.type??item.attribute}/>
                        </View>

                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1}}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'pass_sections'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
                {!change_visible && !draft_player.no_password ?
                <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center', padding:10 }}>
                    <View float style={{ borderRadius:8, backgroundColor:C.views.background, justifyContent:'center', alignItems:'center', padding:30 }}>
                        
                        <Text style={{ marginBottom:30 }} size={24} theme='h1'>Password Set</Text>
                        
                        <Icons.LockClosedIcon size={40} color={C.text.success} />
                        <Button
                            title='CHANGE'
                            style={{ marginTop:20, padding:15 }}
                            type='info'
                            onPress={() => setDraftPassword({ ...draft_password, change_visible: true })}
                        />
                    </View>
                </View>
                :<></>}
            </View>
            {walkthrough ?
            <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                <Button
                    title='EXIT SETUP'
                    type='text'
                    onPress={() => onClose()}
                />
                <TouchableOpacity 
                    disabled={!is_valid}
                    style={is_valid?button_styles.wizard_valid:button_styles.wizard_invalid}
                    onPress={() => handleSetPassword()}
                >
                    <Icons.ChevronIcon size={20} direction='right' color={Colors.brand.midnight}/>
                </TouchableOpacity>
            </View>
            :
            <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                <Button
                    style={{ flex:1, marginRight:5 }}
                    title='BACK'
                    type='close'
                    onPress={() => onClose()}
                />
                <Button
                    style={{ flex:3, opacity:is_valid && password?1:0.5 }}
                    disabled={!is_valid || !password}
                    title='UPDATE'
                    type='success'
                    onPress={() => handleSetPassword()}
                />
            </View>
            }
        </View>
    )
}

export default PasswordManager